import { useEffect } from 'react'
import {
  Alert,
  Button,
  Descriptions,
  Drawer,
  Flex,
  Grid,
  Skeleton,
  Tag,
  Typography,
  theme,
} from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { formatBytes, formatDateTime, formatDuration } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { displayName } from '@/shared/lib/planName'
import { LoadError } from '@/shared/ui/LoadError'
import { SubscriberStateTag } from '@/shared/ui/StatusTag'
import type { RowAction } from '@/shared/ui/RowActions'
import { MaskedPhone } from '@/shared/ui/MaskedPhone'
import { PERM } from '@/shared/auth/partnerPermissions'
import { useAuth } from '@/shared/auth/useAuth'
import { revealSubscriberContact } from '@/shared/api/contactReveal'
import {
  fetchGrants,
  fetchOverview,
  fetchPlans,
  fetchSubscriberContact,
  type ActiveGrant,
  type SubscriberRow,
} from './subscriberApi'
import { subscriberKeys } from './subscriberKeys'
import { SubscriberProfileCard } from './SubscriberProfileCard'

interface SubscriberCardProps {
  row: SubscriberRow | null
  onClose: () => void
  /**
   * Amallar — ro'yxatdagi menyu bilan AYNI ro'yxat. Chaqiruvchi bir
   * marta yasaydi: ikki joyda ikki xil to'plam bo'lsa, qatorda bor
   * amal kartochkada yo'q bo'lib qolardi va buni hech kim sezmasdi.
   */
  actions: readonly RowAction[]
  /** Kartochka ochilgach ma'lum bo'lgan tezlik — amal oynasiga uzatiladi */
  onRateLimitLoaded: (value: string | null) => void
}

function GrantLine({ grant, planLabel }: { grant: ActiveGrant; planLabel: string }) {
  const { t } = useTranslation()
  const parts: string[] = []
  if (grant.remainingBytes !== null) parts.push(formatBytes(grant.remainingBytes))
  if (grant.remainingSeconds !== null) parts.push(formatDuration(grant.remainingSeconds))
  if (grant.expiresAt) parts.push(formatDateTime(grant.expiresAt))

  return (
    <Flex align="center" justify="space-between" gap={8} wrap>
      <Typography.Text>{planLabel}</Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        {/* ⚠️ Bo'sh ro'yxat «limitsiz» degani, «noma'lum» emas: hajm va
            vaqt kvotasi ikkalasi ham `null` bo'lgan grant cheklovsiz. */}
        {parts.length > 0 ? parts.join(' · ') : t('subscribers.card.unlimited')}
      </Typography.Text>
    </Flex>
  )
}

/**
 * Kontakt telefoni — SUKUT BO'YICHA NIQOBLANGAN.
 *
 * <h2>⛔ QAROR O'ZGARDI (egasi, 2026-08-24) — eski izoh nima degani edi</h2>
 * Ilgari bu yerda «niqob serverdan keladi, ochish tugmasi ATAYLAB yo'q»
 * deb yozilgan edi va uning ASOSI ikki da'vo edi. Ikkalasi ham
 * 2026-08-24 da o'lchandi va NOTO'G'RI chiqdi:
 *
 *   · «`ContactEntry.masked()` har O'QISH yo'lida qo'llanadi» — sheriklar
 *     kabineti kontaktni `GET /api/subscribers/{id}/contact` orqali
 *     oladi va o'sha yo'l `.masked()` ni ATAYLAB CHAQIRMAYDI
 *     (`ContactLinkServiceImpl.findForSubscriber:52-56`,
 *     `ContactView.java:9-27`). Ya'ni bu ekranda niqob HECH QACHON
 *     ko'rinmasdi — `isMaskedValue` doim `false` edi va shox o'lik edi;
 *   · «`contact.reveal` sherik to'plamida yo'q» — endi BOR
 *     (`PartnerKind.java:83`).
 *
 * Egasi qarori: raqam niqoblansin, ochish yo'li bo'lsin.
 *
 * <h2>⛔ OCHISH SERVERGA BORADI — auditli yo'l bilan</h2>
 * `POST /api/subscribers/{id}/contact/reveal` (egasi qarori,
 * 2026-08-24): sabab MAJBURIY, amal auditga tushadi, doira serverda
 * tekshiriladi. Umumiy reestrning `/api/contacts/{id}/reveal` yo'li
 * ATAYLAB ishlatilmaydi — u butun tenant doirasini talab qiladi
 * (`TenantWideGuard`) va sherikka baribir 403 berardi
 * (`contactRevealApi.ts` dagi to'liq dalil).
 *
 * ⚠️ Tugma `contact.reveal` huquqi ostida: huquqsiz sherikda u
 * UMUMAN chizilmaydi — «bosilganda 403 beradigan tugma» bu kod
 * bazasida rad etilgan naqsh.
 *
 * ⚠️ Ochilgan qiymat `MaskedPhone` ning LOKAL holatida turadi va
 * kartochka yopilganda yo'qoladi — keshga umuman tushmaydi
 * (`revealSubscriberContact` izohi).
 */
function ContactPhone({
  value,
  onReveal,
}: {
  value: string | null
  onReveal?: (reason: string) => Promise<string | null>
}) {
  return <MaskedPhone value={value} {...(onReveal ? { onReveal } : {})} />
}

/**
 * Abonent kartochkasi.
 *
 * <h2>⚠️ Nima KO'RSATILADI va nima yo'q</h2>
 * Ko'rsatiladi: kim (login, ism, telefon), qanday ulanadi (usul),
 * qancha puli bor, qaysi paket amalda va qancha qolgan, tezlik,
 * nechta qurilma ulangan. Bularning hammasi resellerning kundalik
 * savollariga to'g'ridan-to'g'ri javob beradi.
 *
 * ⚠️ 2026-08-23 dan buyon KONTAKT ham shu yerda: F.I.Sh., telefon
 * (ikkinchisi ham), e-pochta, VILOYAT, TUMAN, manzil, izoh. Egasining
 * talabi: «abonent qaysi viloyat, tuman, address, elektron pochta…
 * to'liq ko'rinsin». Ilgari bu maydonlar FAQAT tahrirlash formasida
 * bor edi, ya'ni ko'rish uchun yozish oynasini ochish kerak edi — va
 * ochilgan forma tasodifiy saqlanib, mavjud kontaktni almashtirib
 * yuborishi mumkin edi.
 *
 * Ko'rsatilmaydi: to'lov tarixi, audit jurnali, grantlar tarixi,
 * sessiyalar ro'yxati, RADIUS atributlari. Ular alohida ekran va
 * alohida so'rov talab qiladi, kartochkani esa uzun varaqlanadigan
 * ro'yxatga aylantirardi — reseller esa bu yerga bitta savol bilan
 * keladi. Kerak bo'lsa keyin qo'shiladi (egasi qarori §10.5).
 *
 * ⚠️ Balans — eng katta element: reseller ham, kassir ham birinchi
 * shunga qaraydi.
 */
export function SubscriberCard({ row, onClose, actions, onRateLimitLoaded }: SubscriberCardProps) {
  const { t, i18n } = useTranslation()
  const { can } = useAuth()
  const { token } = theme.useToken()
  const screens = Grid.useBreakpoint()
  const isMobile = screens.md === false
  const id = row?.subscriber.id ?? null

  /**
   * Kontakt raqamini AUDITLI ochish — `POST /api/subscribers/{id}/contact/reveal`.
   *
   * ⚠️ Tugma FAQAT `contact.reveal` bilan chiziladi: huquqsiz sherikda
   * u har bosishda 403 berardi. Huquq bo'lmasa `onReveal` UZATILMAYDI
   * va `MaskedPhone` ochish tugmasini umuman ko'rsatmaydi.
   *
   * ⛔ SERVER UCHTA RAQAM QAYTARADI VA ULAR BOSHQA-BOSHQA MAYDONLAR.
   * Har chaqiruvchi O'Z maydonini oladi (`shared/api/contactReveal`
   * dagi to'liq dalil):
   *
   *   · `phone`           -> ABONENT yozuvining raqami (SMS manbasi)
   *   · `contactPhone`    -> kontakt bloki
   *   · `contactPhoneAlt` -> kontakt blokidagi ikkinchi raqam
   *
   * ⛔ Ularni almashtirib qo'yish JIMGINA nuqson bo'lardi: maydonlar
   * `.nullish()`, ya'ni sxema yiqilmaydi va ekranda BOSHQA odamning
   * raqami ishonarli ko'rinib turardi.
   *
   * ⚠️ Uchta ayri funksiya, bitta emas: ekranda ular ALOHIDA qatorlar
   * va operator bittasini so'raydi. Bitta chaqiruv hammasini birdan
   * ochib yuborardi.
   */
  const canReveal = can(PERM.contactReveal)
  const revealAccessPhone =
    canReveal && id !== null
      ? (reason: string) => revealSubscriberContact(id, reason).then((r) => r.phone)
      : null
  const revealPhone =
    canReveal && id !== null
      ? (reason: string) => revealSubscriberContact(id, reason).then((r) => r.contactPhone)
      : null
  const revealAlt =
    canReveal && id !== null
      ? (reason: string) => revealSubscriberContact(id, reason).then((r) => r.contactPhoneAlt)
      : null

  const overview = useQuery({
    queryKey: subscriberKeys.overview(id ?? ''),
    queryFn: () => fetchOverview(id ?? ''),
    enabled: id !== null,
  })

  const grants = useQuery({
    queryKey: subscriberKeys.grants(id ?? ''),
    queryFn: () => fetchGrants(id ?? ''),
    enabled: id !== null,
  })

  const plans = useQuery({
    queryKey: subscriberKeys.plans,
    queryFn: fetchPlans,
    staleTime: 5 * 60_000,
    enabled: id !== null,
  })

  /*
    ⚠️ KONTAKT — ALOHIDA SO'ROV VA ALOHIDA KALIT.

    Boshqa servis (`xsib-platform`, ADR-031) va o'z yo'li bilan yiqilishi
    mumkin. `overview` bilan bitta kalitga qo'shilsa kontakt yiqilgan
    kuni butun manzara «xato» bo'lib qolardi — balans ham, tezlik ham
    ko'rinmasdi.

    ⛔ `/api/contacts/**` CHAQIRILMAYDI: u xodimlar reestri va
    `contact.read` ni BUTUN TENANT doirasida talab qiladi
    (`contactDraft.ts` da sabab bilan yozilgan). Bu yo'l esa doirani
    o'zi tekshiradi va `subscriber.read` bilan ochiladi — u
    `PartnerKind.RESELLER` da bor.
  */
  const contactQuery = useQuery({
    queryKey: subscriberKeys.contact(id ?? ''),
    queryFn: ({ signal }) => fetchSubscriberContact(id ?? '', signal),
    enabled: id !== null,
  })

  const contact = contactQuery.data ?? null

  const rateLimitOverride = overview.data?.rateLimitOverride ?? null

  // ⚠️ Chizish PAYTIDA emas, effektda: chaqiruvchining holatini render
  // ichida o'zgartirish React'da cheksiz aylanish beradi. Amal oynasi
  // tezlikni shu yerdan oladi — u ro'yxat qatorida umuman yuklanmagan.
  useEffect(() => {
    onRateLimitLoaded(rateLimitOverride)
  }, [rateLimitOverride, onRateLimitLoaded])

  const planName = (planId: string | null): string => {
    if (planId === null) return t('subscribers.card.manualGrant')
    const plan = plans.data?.find((p) => p.id === planId)
    return plan ? displayName(plan, i18n.language) : t('common.dash')
  }

  const s = row?.subscriber ?? null
  // Kartochkadagi balans OVERVIEW dan: ro'yxatdagi son amaldan keyin
  // eskirgan bo'lishi mumkin, bu so'rov esa kartochka bilan birga
  // yangilanadi.
  const balance = overview.data?.balance ?? row?.balance ?? null

  return (
    <Drawer
      open={row !== null}
      onClose={onClose}
      placement={isMobile ? 'bottom' : 'right'}
      {...(isMobile ? { height: '92%' } : { width: 460 })}
      title={s?.login ?? ''}
      destroyOnHidden
    >
      {s === null ? null : (
        <Flex vertical gap={16}>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              {t('common.balance')}
            </Typography.Text>
            <Typography.Title
              level={2}
              style={{
                margin: 0,
                color: balance && balance.amount < 0 ? token.colorError : undefined,
              }}
            >
              {formatMoneyValue(balance)}
            </Typography.Title>
          </div>

          <Flex gap={6} wrap align="center">
            <SubscriberStateTag state={s.state} />
            <Tag>{s.kind}</Tag>
            <Tag>{row?.planName ?? t('subscribers.noPlan')}</Tag>
          </Flex>

          {overview.isError ? (
            <LoadError titleKey="subscribers.card.overviewFailed" error={overview.error} />
          ) : null}
          {overview.isPending ? <Skeleton active paragraph={{ rows: 3 }} /> : null}

          {overview.data?.staleSession ? (
            // ⚠️ Bu XATO emas: baza to'g'ri, faol sessiya esa tarif
            // o'zgarishidan oldin boshlangan. Sessiyani uzish uni
            // to'g'rilaydi va tugma amallar ro'yxatida.
            <Alert type="warning" showIcon message={t('subscribers.card.staleSession')} />
          ) : null}

          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={t('subscribers.form.login')}>
              <Typography.Text copyable>{s.login}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('subscribers.form.displayName')}>
              {s.displayName ?? t('common.dash')}
            </Descriptions.Item>
            {/* ⛔ ABONENTNING raqami ham NIQOBLANADI (egasi qarori,
                2026-08-24). Server uni niqoblamaydi va bu ONGLI qaror
                (`Subscriber.java:44-49`: «chegara — ko'rinish doirasi,
                niqob emas»), ya'ni niqob BU YERDA, chizish paytida
                qo'yiladi.

                ⚠️ Bu raqam kontakt raqamidan BOSHQA maydon: SMS aynan
                undan oqadi (ADR-031 §2) va ikkalasi bir xil bo'lishi
                SHART EMAS. Shuning uchun ikkita alohida qator, ikkalasi
                ham niqob bilan. */}
            <Descriptions.Item label={t('subscribers.form.phone')}>
              {/* ⛔ MAYDON `phone` — kontaktniki EMAS. Ilgari bu yerda
                  LOKAL ochish turardi, chunki bu raqamning ochish yo'li
                  umuman yo'q edi; 2026-08-24 dan u ham auditli
                  endpointdan keladi. */}
              <MaskedPhone
                value={s.phone}
                {...(revealAccessPhone ? { onReveal: revealAccessPhone } : {})}
              />
            </Descriptions.Item>
            <Descriptions.Item label={t('subscribers.card.rateLimit')}>
              {overview.data?.rateLimit ?? t('common.dash')}
              {rateLimitOverride ? (
                // ⚠️ Qo'lda qo'yilgani ALOHIDA belgilanadi: aks holda
                // tarifni almashtirgan reseller tezlik nega
                // o'zgarmaganini tushunmasdi.
                <Tag color="gold" style={{ marginInlineStart: 8 }}>
                  {t('subscribers.card.rateLimitOverride')}
                </Tag>
              ) : null}
            </Descriptions.Item>
            <Descriptions.Item label={t('subscribers.card.sessions')}>
              {overview.data?.activeSessions ?? t('common.dash')}
            </Descriptions.Item>
            <Descriptions.Item label={t('subscribers.card.accessUntil')}>
              {formatDateTime(overview.data?.accessUntil)}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.createdAt')}>
              {formatDateTime(s.createdAt)}
            </Descriptions.Item>
          </Descriptions>

          {/* ⚠️ ALOHIDA BO'LIM, yuqoridagi jadvalga QO'SHILMAGAN.

              Yuqorisi — abonent yozuvining o'zi (`subscriber` sxemasi),
              bu esa KONTAKT (`platform.contact`, boshqa servis, boshqa
              so'rov). Ular bitta jadvalda tursa yuklanish, xato va
              «yo'q» holatlari bitta bo'lib ko'rinardi: kontakt
              yiqilganda login ham «—» bo'lib qolardi.

              ⚠️ Yuqoridagi «Telefon» — ABONENTNING raqami: SMS aynan
              undan oqadi (ADR-031 §2) va u NIQOBLANMAYDI. Bu yerdagi
              telefon esa kontakt yozuvidan va u har o'qishda
              niqoblangan holda keladi. Ikkalasi bir xil bo'lishi SHART
              EMAS. */}
          <div>
            <Typography.Text strong>{t('subscribers.card.contact')}</Typography.Text>

            {contactQuery.isError ? (
              /* ⛔ 403 «kontakt yo'q» EMAS. `fetchSubscriberContact` uni
                 ataylab yuqoriga o'tkazadi (faqat 404 da `null`), va bu
                 yerda u XATO bo'lib ko'rinadi. «Ma'lumot yo'q» deb
                 ko'rsatish operatorga bazada hech narsa yo'qdek
                 tuyulardi va u mavjud kontaktni qaytadan yozardi. */
              <LoadError titleKey="subscribers.card.contactFailed" error={contactQuery.error} />
            ) : null}
            {contactQuery.isPending ? <Skeleton active paragraph={{ rows: 2 }} /> : null}

            {contactQuery.isSuccess && contact === null ? (
              // ⚠️ BO'SH JOY EMAS, AYTILGAN GAP: kontakt yo'qligi normal
              // holat va operator uni shu yerdan qo'sha oladi.
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 6 }}>
                {t('subscribers.card.contactNone')}
              </Typography.Paragraph>
            ) : null}

            {contact === null ? null : (
              /* ⚠️ Bo'sh maydon ham QATOR bo'lib qoladi («—»): qatorni
                 umuman chizmaslik «e-pochta kiritilmagan» bilan
                 «e-pochta ko'rsatilmaydi» ni ajratib bo'lmaydigan
                 qilardi. */
              <Descriptions
                column={1}
                size="small"
                bordered
                style={{ marginTop: 6 }}
                // ⚠️ Manzil va e-pochta probelsiz uzun bo'lishi mumkin
                // (240 va 190 belgi). Uzilishsiz ular 460px lik panelni
                // gorizontal aylantiruvchi qilib qo'yardi.
                contentStyle={{ wordBreak: 'break-word' }}
              >
                <Descriptions.Item label={t('subscribers.form.contactFullName')}>
                  {contact.fullName ?? t('common.dash')}
                </Descriptions.Item>
                <Descriptions.Item label={t('subscribers.form.contactPhone')}>
                  <ContactPhone value={contact.phone} {...(revealPhone ? { onReveal: revealPhone } : {})} />
                </Descriptions.Item>
                <Descriptions.Item label={t('subscribers.form.contactPhoneAlt')}>
                  <ContactPhone value={contact.phoneAlt} {...(revealAlt ? { onReveal: revealAlt } : {})} />
                </Descriptions.Item>
                <Descriptions.Item label={t('subscribers.form.contactEmail')}>
                  {/* ⚠️ E-pochta NIQOBLANMAYDI (`ContactEntry.masked()`
                      faqat telefonga tegadi) — ya'ni nusxa olish
                      xavfsiz va u kerak: operator undan xat yozadi. */}
                  {contact.email ? (
                    <Typography.Text copyable>{contact.email}</Typography.Text>
                  ) : (
                    t('common.dash')
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t('subscribers.form.contactRegion')}>
                  {/* ⚠️ NOM serverdan tayyor keladi (`regionName`) — bu
                      yerda `geo.read` so'ralmaydi. Reestr yopiq bo'lsa
                      ham viloyat NOMI ko'rinadi, xom UUID emas. */}
                  {contact.regionName ?? t('common.dash')}
                </Descriptions.Item>
                <Descriptions.Item label={t('subscribers.form.contactDistrict')}>
                  {contact.districtName ?? t('common.dash')}
                </Descriptions.Item>
                <Descriptions.Item label={t('subscribers.form.contactAddress')}>
                  {contact.address ?? t('common.dash')}
                </Descriptions.Item>
                <Descriptions.Item label={t('subscribers.form.contactNote')}>
                  {/* Izoh uzun bo'lishi mumkin (500 belgi) — qator
                      uzilishi saqlanadi, aks holda u bitta uzun satr
                      bo'lib kartochkani gorizontal cho'zardi. */}
                  {contact.note ? (
                    <Typography.Text style={{ whiteSpace: 'pre-wrap' }}>
                      {contact.note}
                    </Typography.Text>
                  ) : (
                    t('common.dash')
                  )}
                </Descriptions.Item>
              </Descriptions>
            )}
          </div>

          <SubscriberProfileCard subscriberId={s.id} profile={s.profile} />

          <div>
            <Typography.Text strong>{t('subscribers.card.grants')}</Typography.Text>
            {grants.isError ? (
              <LoadError titleKey="subscribers.card.grantsFailed" error={grants.error} />
            ) : null}
            {grants.isPending ? <Skeleton active paragraph={{ rows: 2 }} /> : null}
            {grants.isSuccess && grants.data.usable.length === 0 ? (
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                {t('subscribers.card.noGrants')}
              </Typography.Paragraph>
            ) : null}
            <Flex vertical gap={6} style={{ marginTop: 6 }}>
              {(grants.data?.usable ?? []).map((grant) => (
                <GrantLine key={grant.id} grant={grant} planLabel={planName(grant.planId)} />
              ))}
            </Flex>
          </div>

          {/* ⚠️ Kartochkada amallar TUGMA bo'lib chiziladi, uch nuqta
              menyusi emas: bu yerda joy bor va reseller kartochkani
              aynan amal qilish uchun ochadi. Ro'yxat esa BIR JOYDA
              yasaladi — chaqiruvchida. */}
          <Flex vertical gap={8}>
            {actions
              .filter((action) => action.hidden !== true)
              .map((action) => (
                <Button
                  key={action.key}
                  block
                  size="large"
                  icon={action.icon}
                  danger={action.danger ?? false}
                  disabled={action.disabled ?? false}
                  onClick={action.onClick}
                >
                  {action.label}
                </Button>
              ))}
          </Flex>
        </Flex>
      )}
    </Drawer>
  )
}
